<template>
  <Card>
    <div slot="body">
      <p class="text-center text-md-left text-muted mb-4">เริ่มต้นด้วยการอัพโหลดไฟล์ CSV</p>

      <div class="row">
        <div class="col-12 col-md-4 d-flex flex-column gap-4">
          <!-- Default Weight Setting -->
          <div class="form-group">
            <label class="font-weight-bold">น้ำหนักปกติ (กรัม):</label>
            <input type="number"
                   v-model.number="defaultWeight"
                   class="form-control"
                   min="1" />
            <small class="form-text text-muted">น้ำหนักที่ใช้หากไม่มีข้อมูลน้ำหนักในไฟล์</small>
          </div>

          <div class="row mb-4">
            <div class="col-md-6 offset-md-3">

            </div>
          </div>

          <!-- Shop and Shipping Method Selection -->
          <div class="form-group">
            <label class="font-weight-bold">เลือกร้านค้า: <span class="text-danger">*</span></label>
            <select v-model="selectedShopId"
                    class="form-control form-control-lg"
                    required
                    @change="handleShopChange">
              <option value="">-- กรุณาเลือกร้านค้า --</option>
              <option v-for="shop in shopOptions"
                      :key="shop.value"
                      :value="shop.value">
                {{ shop.name }}
              </option>
            </select>
          </div>

          <div class="form-group"
               v-if="selectedShopId">
            <label class="font-weight-bold">วิธีการจัดส่ง: <span class="text-danger">*</span></label>
            <ShippingMethodInput :options="availableShippingMethods"
                                 :values="selectedShippingMethod"
                                 v-model="selectedShippingMethod"
                                 variant="info"
                                 size="sh"
                                 justify="left"
                                 :multiple="false" />
          </div>
        </div>

        <div class="col-12 col-md-8 d-flex flex-column">
          <!-- Upload Area using DropZone -->
          <div :class="['dropzone-wrapper', { 'is-disabled': !canUpload }]">
            <DropZone ref="dropzone"
                      :value="uploadedFiles"
                      label="อัพโหลดไฟล์ CSV ที่นี่ หรือคลิกเพื่อเลือกไฟล์"
                      size="lg"
                      :clearOption="true"
                      :localOnly="true"
                      @file-added="handleFileAdded" />
            <div v-if="!canUpload"
                 class="dropzone-overlay d-flex flex-column justify-content-center align-items-center text-center p-3">
              <p class="p-2 font-weight-bold bg-secondary"
                 style="border-radius: 0.5rem;">กรุณาเลือกร้านค้าและวิธีการจัดส่งก่อนอัพโหลดไฟล์</p>
              <small class="text-muted p-1">เลือกค่าด้านซ้าย แล้วค่อยอัพโหลดไฟล์ CSV</small>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <h5 class="mb-2">อัพเดทสถานะการจัดส่ง</h5>

          <div class="d-flex">
            <DatePicker id="date-range-from"
                        custom_class="align-self-center mr-2 mb-0"
                        v-model="deliveryStatusReport.fromDate"
                        :placeholder="'เลือกวันที่เริ่ม'" />

            <DatePicker id="date-range-to"
                        custom_class="align-self-center mr-2 mb-0"
                        v-model="deliveryStatusReport.toDate"
                        :placeholder="'เลือกวันที่สิ้นสุด'" />


            <Select id="tiktoksof-order-status"
                    form_group_class="align-self-center mr-4 mb-0"
                    :options="statusOptions"
                    v-model="deliveryStatusReport.status" />

            <Button id="btnGetDeliveryStatusReport"
                    custom_class="align-self-center"
                    variant="info"
                    size="md"
                    :disabled="deliveryStatusReport.loading"
                    :loading="deliveryStatusReport.loading"
                    @click="getDeliveryStatusReport()">โหลดไฟล์ excel</Button>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import moment from "moment-timezone";
import Card from "@/components/core/Card";
import Button from "@/components/core/Button";
import Select from "@/components/core/Select";
import ShippingMethodInput from "@/components/derived/ShippingMethodInput";
import DropZone from "@/components/derived/DropZone";
import { Toast } from "@/helpers/toastr";
import { shippingInfo } from "@/helpers/core";
import DatePicker from "@/components/core/DatePicker";
import { parseCSV, exportOrdersToTiktokSofDeliveredExcel, isValidTrackingNo } from "./helpers";
import { i18n } from "@/i18n";
import { getOrderStatus } from "@/helpers/core";

export default {
  name: "UploadScreen",
  components: { Card, Button, Select, ShippingMethodInput, DropZone, DatePicker },
  props: {
    shops: {
      type: Array,
      default: () => []
    },
    shippingMethods: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedShopId: "",
      selectedShippingMethod: [],
      defaultWeight: 500,
      uploadedFiles: [],
      availableShippingMethods: [],

      deliveryStatusReport: {
        fromDate: null,
        toDate: null,
        status: null,
        loading: false
      }
    };
  },
  computed: {
    shopOptions() {
      if (!this.shops) return [];
      return this.shops.map(shop => ({
        value: shop.id,
        name: shop.details.name
      }));
    },
    canUpload() {
      return !!(this.selectedShopId && this.selectedShippingMethod && this.selectedShippingMethod.length > 0);
    },
    statusOptions() {
      let arr = [{ name: i18n.t("tabs.all"), value: 0 }];
      let ORD_STS = getOrderStatus();
      Object.keys(ORD_STS).forEach((status) => {
        arr.push({ name: ORD_STS[status].title, value: status });
      });
      return arr;
    }
  },
  methods: {
    handleShopChange() {
      // Reset shipping method when shop changes
      this.selectedShippingMethod = [];

      // Update shipping methods based on selected shop
      const shop = this.shops.find(s => s.id === this.selectedShopId);
      if (shop && shop.details.shippingMethods && shop.details.shippingMethods.length) {
        let shippingMethods = shippingInfo("all");
        shippingMethods = shippingMethods.map((sm) => {
          sm.available = shop.details.shippingMethods.includes(sm.name);
          return sm;
        });
        this.availableShippingMethods = shippingMethods;

        // Auto-select first available shipping method
        const firstAvailable = this.availableShippingMethods.find(sm => sm.available);
        if (firstAvailable) {
          this.selectedShippingMethod = [firstAvailable.name];
        }
      } else {
        this.availableShippingMethods = [];
      }
    },
    handleFileAdded(file) {
      if (!this.canUpload) return;

      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.csv')) {
        Toast('กรุณาเลือกไฟล์ CSV เท่านั้น', 'danger');
        this.$refs.dropzone.clear();
        return;
      }

      if (!this.selectedShopId) {
        Toast('กรุณาเลือกร้านค้าก่อน', 'danger');
        this.$refs.dropzone.clear();
        return;
      }

      if (!this.selectedShippingMethod || this.selectedShippingMethod.length === 0) {
        Toast('กรุณาเลือกวิธีการจัดส่งก่อน', 'danger');
        this.$refs.dropzone.clear();
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const { csvData, groupedOrders } = parseCSV(text);

          if (csvData.length === 0) {
            Toast('ไม่พบข้อมูลในไฟล์ CSV', 'danger');
            return;
          }

          this.$emit('file-uploaded', {
            rowCount: groupedOrders.length,
            fileName: file.name,
            csvData: csvData,
            groupedOrders: groupedOrders,
            shopId: this.selectedShopId,
            shippingMethod: this.selectedShippingMethod[0], // Use first item from array
            defaultWeight: this.defaultWeight
          });
        } catch (error) {
          Toast('ไม่สามารถอ่านไฟล์ CSV ได้', 'danger');
        }
      };
      reader.onerror = () => {
        Toast('ไม่สามารถอ่านไฟล์ CSV ได้', 'danger');
      };
      reader.readAsText(file, 'UTF-8');
    },

    preventUploadIfBlocked() { },

    getDeliveryStatusReport() {
      this.deliveryStatusReport.loading = true;

      let from = moment
        .tz(this.deliveryStatusReport.fromDate, "Asia/Bangkok")
        .startOf("day")
        .valueOf();
      let to = moment
        .tz(this.deliveryStatusReport.toDate, "Asia/Bangkok")
        .endOf("day")
        .valueOf();
      if (!from || !to) {
        Toast("From & To dates must exist", "danger");
        this.deliveryStatusReport.loading = false;
        return;
      }
      this.$store.dispatch("orders/fetchOrdersReport", {
        from,
        to,
        status: this.deliveryStatusReport.status,
        shopId: "all",
        format: "json",
        callback: (status, data) => {
          if (status) {
            console.log(data);
            const items = (data && Array.isArray(data.Items)) ? data.Items : [];
            const reportData = items
              .filter(item => item && item.details && item.details.isTiktokSof === true)
              .map(item => {
                const rawTrackingNo = item.trackingNo || item.details.trackingNo || null;
                return {
                  orderId: item.details.orderCode,
                  trackingNo: isValidTrackingNo(rawTrackingNo) ? String(rawTrackingNo).trim() : null,
                  status: item.details.status !== undefined ? item.details.status : item.status,
                  returnReason: item.details.returnReason || item.returnReason || null,
                  products: item.details.products || {}
                };
              })
              .filter(item => isValidTrackingNo(item.trackingNo));

            const finalReportData = [];
            for (const reportDataItem of reportData) {
              const products = Object.values(reportDataItem.products || {});
              if (products.length > 0) {
                for (const product of products) {
                  finalReportData.push({
                    orderId: reportDataItem.orderId,
                    trackingNo: reportDataItem.trackingNo,
                    status: reportDataItem.status,
                    returnReason: reportDataItem.returnReason,
                    productName: product.name || '',
                    productSku: product.sku || ''
                  });
                }
              } else {
                finalReportData.push({
                  orderId: reportDataItem.orderId,
                  trackingNo: reportDataItem.trackingNo,
                  status: reportDataItem.status,
                  returnReason: reportDataItem.returnReason,
                  productName: '',
                  productSku: ''
                });
              }
            }

            if (finalReportData.length === 0) {
              Toast("ไม่พบข้อมูลการจัดส่งของ TikTok SOF ในช่วงวันที่เลือก", "warning");
              this.deliveryStatusReport.loading = false;
            } else {
              console.log("Report data:", finalReportData);
              exportOrdersToTiktokSofDeliveredExcel(finalReportData);
            }
          }
          this.deliveryStatusReport.loading = false;
        },
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.dropzone-wrapper {
  position: relative;
}

.dropzone-wrapper.is-disabled {
  pointer-events: none;
  filter: grayscale(0.2);
}

.dropzone-wrapper .dropzone-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  pointer-events: auto;
}

.dropzone-wrapper.is-disabled .dropzone-overlay {
  pointer-events: auto;
}

.dropzone-wrapper .dropzone-overlay p {
  margin-bottom: 0.35rem;
}
</style>
